import { useEffect, useMemo, type FunctionComponent } from "react";
import { Form, Input, Select } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { FitnessAppTask } from "@fitness-app/data-models/entities/Automation";
import { ProgramAutomationScheduleAnchor } from "@fitness-app/data-models/entities/ProgramAutomation";

import { useEmailLists } from "~/hooks/useEmailLists";
import { useTagOptions } from "~/hooks/useTagOptions";
import { ScheduleTimingFields } from "../ScheduleTimingFields/ScheduleTimingFields";
import { TimeField } from "../TimeField/TimeField";
import { type FitnessAppTaskFormModel } from "./types";

interface OwnProps {
  formController?: FormInstance<FitnessAppTaskFormModel>;
  onSubmit: (formData: FitnessAppTaskFormModel) => void;
  model?: Partial<FitnessAppTaskFormModel> | null;
}

type Props = OwnProps;

const FitnessAppTaskForm: FunctionComponent<Props> = ({ formController, onSubmit, model }) => {
  const { t } = useTranslation(["products", "common", "automation"]);
  const { options: traineeTags } = useTagOptions("traineeTags");
  const { tags } = useEmailLists();

  const fitnessAppTaskOptions = useMemo(() => {
    return [
      {
        label: t(`common:fitnessAppTask.${FitnessAppTask.AddTagsToTraineeProfile}`),
        value: FitnessAppTask.AddTagsToTraineeProfile,
      },
      {
        label: t(`common:fitnessAppTask.${FitnessAppTask.RemoveTagsFromTraineeProfile}`),
        value: FitnessAppTask.RemoveTagsFromTraineeProfile,
      },
    ];
  }, [t]);

  useEffect(() => {
    if (model) {
      formController?.setFieldsValue(model);
    } else {
      formController?.resetFields();
    }
  }, [model]);

  return (
    <div>
      <Form<FitnessAppTaskFormModel>
        name="fitness-app-task-form"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        layout="horizontal"
        form={formController}
        initialValues={{
          description: "",
          sentTimeConfig: { mode: "absolute", avoidSendingOnTheNight: true },
          scheduleTiming: {
            anchor: ProgramAutomationScheduleAnchor.WeekDay,
          },
          ...model,
        }}
        onFinish={onSubmit}
      >
        <Form.Item
          name={["action", "type"]}
          label={t<string>("productAutomation.form.taskType")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select options={fitnessAppTaskOptions} />
        </Form.Item>

        <Form.Item
          name={["action", "tags"]}
          label={t<string>("productAutomation.form.clientTags")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select
            notFoundContent={false}
            options={[...traineeTags, ...tags]}
            mode="tags"
            placeholder={t<string>("products:form.clientTagsPlaceholder")}
          />
        </Form.Item>

        <ScheduleTimingFields formController={formController} />

        <TimeField showOnlyAbsolute={false} label={t<string>("automation:automatedMessageForm.operationTime")} />

        <Form.Item name="description" label={t<string>("productAutomation.form.description")}>
          <Input.TextArea rows={3} placeholder={t<string>("productAutomation.form.descriptionTaskPlaceholder")} />
        </Form.Item>
      </Form>
    </div>
  );
};

export default FitnessAppTaskForm;
